import React, { useState, useEffect } from 'react';
import { Menu, X, Phone } from 'lucide-react';
import { logoUrl, menuItems, contactInfo } from '../data/mock';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const isGalleryPage = location.pathname === '/galeri';
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e, href) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      if (isGalleryPage) {
        window.location.href = '/' + href;
        return;
      }
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
      setIsMenuOpen(false);
    }
  };

  // On homepage, header is transparent until scrolled
  const headerBg = isHomePage && !isScrolled && !isMenuOpen
    ? 'bg-black/20 backdrop-blur-sm'
    : 'bg-white/95 backdrop-blur-md shadow-sm';

  const textColor = isHomePage && !isScrolled && !isMenuOpen
    ? 'text-white'
    : 'text-gray-700';

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerBg}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img 
              src={logoUrl} 
              alt="Nova Eğitim Logo" 
              className="h-14 md:h-16 w-auto object-contain transition-all group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {menuItems.map((item) => (
              item.href.startsWith('/') ? (
                <Link
                  key={item.label}
                  to={item.href}
                  className={`${textColor} hover:text-red-500 font-medium transition-colors relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-red-500 hover:after:w-full after:transition-all`}
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className={`${textColor} hover:text-red-500 font-medium transition-colors relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-red-500 hover:after:w-full after:transition-all`}
                >
                  {item.label}
                </a>
              )
            ))}
          </nav>

          {/* Phone CTA - Desktop */}
          <a 
            href={`tel:${contactInfo.phone}`}
            className="hidden md:flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-all hover:shadow-lg hover:shadow-red-600/25"
          >
            <Phone className="w-4 h-4" />
            <span>Ara</span>
          </a>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`md:hidden p-2 ${textColor} hover:text-red-500 transition-colors`}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <div className={`md:hidden overflow-hidden transition-all duration-300 ${isMenuOpen ? 'max-h-96 pb-4' : 'max-h-0'}`}>
          <nav className="flex flex-col gap-2 bg-white/95 backdrop-blur-md rounded-lg p-2 mt-2">
            {menuItems.map((item) => (
              item.href.startsWith('/') ? (
                <Link
                  key={item.label}
                  to={item.href}
                  onClick={() => setIsMenuOpen(false)}
                  className="text-gray-700 hover:text-red-600 hover:bg-red-50 font-medium py-3 px-4 rounded-lg transition-colors"
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className="text-gray-700 hover:text-red-600 hover:bg-red-50 font-medium py-3 px-4 rounded-lg transition-colors"
                >
                  {item.label}
                </a>
              )
            ))}
            <a 
              href={`tel:${contactInfo.phone}`}
              className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white py-3 px-4 rounded-lg font-medium transition-colors mt-2"
            >
              <Phone className="w-4 h-4" />
              <span>{contactInfo.phone}</span>
            </a>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
